import { useEffect, useRef } from "react";
import { FC, Props } from "./typings";
import { axiosInstance } from "api/axios";
import { getUserId } from "utils/getUserId";
import { useAuth } from "react-oidc-context";
import { getKeys } from "utils/getKeys";
import * as openpgp from "openpgp";
import { readAsBase64 } from "utils/readAsBase64";
import { readFileFromBase64 } from "utils/readFileFromBase64";
import { generateSingleBinary } from "utils/GenerateSingleBinary";
import { reEncryptObjectKeyForPublicKeys } from "utils/reEncryptObjectKeyForPublicKeys";
import { invalidateDecryptedKeys } from "utils/decryptPackage";
import { onCompanyPrivateKeysChanged } from "utils/companyContentKeys";
import { processEbiKeyRequests, resetEbiKeyShareAvailability } from "utils/ebiShareKeys";
import { SignalRContext } from "../../App";
import { HubCallbacksNames } from "api/hub";

/** Zapasowe odpytanie kolejki EBI — gdy sygnał z huba nie dotarł (np. nikt nie był online przy nadaniu). */
const EBI_POLL_MS = 10 * 60 * 1000;

const packageSize = 20;

interface IKey {
  PublicKey: string;
  PrivateKey: string;
  cleanPublicKey: openpgp.key.KeyResult;
  cleanPrivateKey: openpgp.key.KeyResult;
}

interface IAwaitingObjectKey {
  Id: string;
  PrivateKey: string;
}

interface IAwaitingRecovery {
  Id: string;
  UserLabel: string;
  PublicKeys: string[];
  /** Klucz wątku — zostaje dla zgodności ze starszym backendem. */
  PrivateKey: string;
  /** Komplet kluczy obiektów wątku (klucz wątku + klucze przesyłek). */
  Keys?: IAwaitingObjectKey[];
}

interface IWorkspaceMemberAwaitingKeys {
  MemberId: string;
  WorkspaceId: string;
  WorkspaceName?: string;
  /** Klucz publiczny zespołu (base64, binarny) — szyfruje udostępniany klucz prywatny profilu. */
  WorkspacePublicKey?: string;
}

const generateKey = async (myKeys: any): Promise<IKey> => {
  const options: openpgp.KeyOptions = {
    userIds: [
      {
        name: "1",
      },
    ],
    curve: "secp256k1",
    numBits: 2048,
  };

  const key = await openpgp.generateKey(options);
  const pubDecoded = await openpgp.key.readArmored(key.publicKeyArmored);
  const privDecoded = await openpgp.key.readArmored(key.privateKeyArmored);
  const publicKeyResultString = new Uint8Array(
    pubDecoded.keys[0].toPacketlist().write(),
  );
  const keyResultString = new Uint8Array(
    privDecoded.keys[0].toPacketlist().write(),
  );

  const masterPublicKey = await readFileFromBase64(
    myKeys!.publicBase64Key,
    "application/other",
  );
  const armored = new TextDecoder().decode(masterPublicKey);

  const t = (await openpgp.key.readArmored(armored)).keys[0];

  const { message } = await openpgp.encrypt({
    message: await openpgp.message.fromBinary(keyResultString),
    publicKeys: [t],
    armor: false,
  });

  return {
    PublicKey: await readAsBase64(publicKeyResultString),
    PrivateKey: await readAsBase64(new Uint8Array(message.packets.write())),
    cleanPublicKey: pubDecoded,
    cleanPrivateKey: privDecoded,
  };
};

const generateKeysPackage = async (myKeys: any): Promise<IKey[]> => {
  return new Promise(async (resolve) => {
    const packs: IKey[] = [];
    let interval: any = null;
    interval = setInterval(async () => {
      packs.push(await generateKey(myKeys));

      if (packs.length >= packageSize) {
        clearInterval(interval);
        resolve(packs);
      }
    }, 5000);
  });
};

const KeysManager: FC<Props> = ({}) => {
  const auth = useAuth();
  const userId = getUserId(auth.user!);

  const isSharingKeysRef = useRef(false);

  // const manageKeys = async () => {
  //   const myKeys = await getKeys(userId!);
  //   console.log("wchodze");
  //   if (!myKeys) return;

  //   const missingKeys = await axiosInstance.get("/account/missingKeys");

  //   let counter = missingKeys.data.Count;
  //   console.log(counter);
  //   if (counter <= 0) return;

  //   let genTokens = true;
  //   console.log("rozpoczynam generowanie");
  //   while (genTokens) {
  //     if (counter > 0) {
  //       const packs = await generateKeysPackage(myKeys);

  //       const keys = packs.map((p) => {
  //         return {
  //           Id: userId,
  //           PrivateKey: p.PrivateKey,
  //           PublicKey: p.PublicKey,
  //           Initial: true,
  //           Armored: false,
  //         };
  //       });

  //       await axiosInstance.post("/account/userKeys", {
  //         Keys: keys,
  //       });
  //     }

  //     counter = counter - packageSize;

  //     if (counter <= 0) {
  //       genTokens = false;
  //     }
  //   }
  // };

  // Udostępnienie kluczy konwersacji stronie zewnętrznej: ktoś przyjął zaproszenie albo
  // odzyskuje dostęp po utracie kodu. Klucz obiektu potrafi przeszyfrować wyłącznie
  // przeglądarka uczestnika, który już go ma — backend nie zna materiału prywatnego.
  const shareContactFormKeys = async () => {
    // Nie pozwalamy dwóm przebiegom (montaż + push z websocketu) wejść sobie w drogę.
    if (isSharingKeysRef.current) return;
    isSharingKeysRef.current = true;

    try {
      const myKeys = await getKeys(userId!);
      if (!myKeys) return;

      const response = await axiosInstance.get(
        "/settings/contactForm/awaitingRecovery",
      );

      const data = (response.data ?? []) as IAwaitingRecovery[];
      if (!data.length) return;

      // Wcześniej było tu `await data.forEach(async …)` — forEach ignoruje zwracane
      // Promise, więc await nie czekał na nic, błędy nie były łapane, a funkcja kończyła
      // się ZANIM cokolwiek poleciało do backendu.
      for (const item of data) {
        try {
          // Starszy backend zwraca tylko klucz wątku; nowszy komplet kluczy przesyłek.
          // Bez kompletu strona zewnętrzna odszyfruje wyłącznie pierwszą wiadomość.
          const keysToRotate = item.Keys?.length
            ? item.Keys
            : [{ Id: "", PrivateKey: item.PrivateKey }];

          const rotated = [];

          // Fail-closed: backend po zapisie oznacza żądanie jako obsłużone i ustawia
          // chat.PublicKey, więc wysłanie CZĘŚCI kluczy zamknęłoby sprawę, a reszta nigdy
          // by się nie przeszyfrowała. Jeśli choć jeden klucz nie przechodzi, nie wysyłamy
          // nic — pozycja zostaje w kolejce i spróbujemy ponownie.
          for (const key of keysToRotate) {
            rotated.push({
              Id: key.Id,
              PrivateKey: await reEncryptObjectKeyForPublicKeys(
                key.PrivateKey,
                myKeys,
                item.PublicKeys,
              ),
            });
          }

          await axiosInstance.post("/settings/contactForm/awaitingRecovery", {
            Id: item.Id,
            // Pole zachowane dla starszego backendu, który nie zna listy Keys.
            PrivateKey: rotated[0]?.PrivateKey,
            Keys: item.Keys?.length ? rotated : undefined,
          });

          // Cache odszyfrowanych kluczy trzyma wersje sprzed przeszyfrowania.
          invalidateDecryptedKeys(
            rotated.map((k) => k.Id).filter((id) => !!id),
          );
        } catch (error) {
          console.error(
            "Nie udało się udostępnić kluczy konwersacji",
            item.Id,
            error,
          );
        }
      }
    } finally {
      isSharingKeysRef.current = false;
    }
  };

  // EBI (etap 4): przeszyfrowanie przesyłek dla petentów. Klucze prywatne firmy dojeżdżają asynchronicznie
  // (App.tsx: granty → odszyfrowanie), zwykle PO montażu KeysManagera — dlatego też po ich wczytaniu.
  const shareEbiKeys = () => {
    if (!userId) return;
    processEbiKeyRequests(userId).catch((error) =>
      console.error("EBI: obsługa kolejki przeszyfrowań", error),
    );
  };

  useEffect(() => {
    //manageKeys();
    shareContactFormKeys();
  }, []);

  // Druga strona właśnie przyjęła zaproszenie i czeka pod otwartą konwersacją.
  SignalRContext.useSignalREffect(
    HubCallbacksNames.ContactFormKeysToShare,
    () => {
      shareContactFormKeys();
    },
    [],
  );

  // Ponowne odpytanie kolejki przy każdym (re)połączeniu websocketu.
  //
  // Push `contactFormKeysToShare` leci WYŁĄCZNIE wtedy, gdy w chwili przyjęcia zaproszenia
  // Users.Statistics.IsWebsocketOnline == true. Flagę przestawia MainHub w OnConnected/
  // OnDisconnected, jest per-użytkownik (nie per-połączenie) i nie ma heartbeatu, więc:
  //  - urwane połączenie (uśpiony laptop, blip sieci) albo zamknięcie jednej z kilku kart
  //    ustawia ją na false, choć aplikacja jest otwarta → backend wyśle mail zamiast pusha,
  //    a otwarta karta nigdy się o zadaniu nie dowie;
  //  - twarde ubicie procesu zostawia ją na true → push idzie w próżnię I mail nie wychodzi.
  // W obu przypadkach zadanie wisiałoby do pełnego przeładowania strony, bo montaż
  // KeysManagera zdarza się raz na wejście do aplikacji.
  useEffect(() => {
    if (!SignalRContext.connection?.connectionId) return;
    shareContactFormKeys();
    shareEbiKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SignalRContext.connection?.connectionId]);

  useEffect(() => {
    shareEbiKeys();
    // nowe klucze firmy (grant, rotacja) mogą zmienić odmowę BE — karta próbuje od nowa
    const unsubscribe = onCompanyPrivateKeysChanged(() => {
      resetEbiKeyShareAvailability();
      shareEbiKeys();
    });
    const timer = window.setInterval(shareEbiKeys, EBI_POLL_MS);

    return () => {
      unsubscribe();
      window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  SignalRContext.useSignalREffect(
    HubCallbacksNames.EbiKeysToShare,
    () => {
      shareEbiKeys();
    },
    [userId],
  );

  return <></>;
};

export default KeysManager;
